import { Dispatch, SetStateAction, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Card, NavigationItem, toast, VerticalContainer } from '@/components';
import { ChildIcon, ManIcon, PfaLogoIcon, WomanIcon } from '@/components/Icons';
import { SelectChildDto } from '@/shared/interfaces';
import { HouseholdMemberType, SexEnum } from '@/shared/enums';
import { adolescentMinimumAgeAtom } from '@/caregiverSpace/shared/model';
import { R as ProfileResources } from '@/features/profile/resources/ProfileResources.resx';
import { useProfile } from '@/features/shared/lib/useProfile';
import { R as BookingResources } from '@/features/booking/resources/Texts.resx';
import { AddChildrenModal } from '@/features/shared/features/addOrEditChild/ui';
import { AddChildrenMode } from '@/features/shared/features/addOrEditChild/lib';
import {
    format,
    R,
} from '@/features/pfaPhysiotherapy/resources/PfaPhysiotherapyResources.resx';
import { AdultHouseholdMemberWarningModal } from '@/features/booking/widgets/select-patient/ui/AdultHouseholdMemberWarningModal';

type SelectPfaPatientProps = {
    onSelectPatient: (patientUid: string) => void;
    parentUid: string;
    parentFirstName: string;
    parentGender: SexEnum;
    children: SelectChildDto[];
    setChildren: Dispatch<SetStateAction<SelectChildDto[]>>;
};

export const SelectPfaPatient = ({
    onSelectPatient,
    parentUid,
    parentFirstName,
    parentGender,
    children,
    setChildren,
}: SelectPfaPatientProps) => {
    const { refetch } = useProfile();
    const [showAddChildModal, setShowAddChildModal] = useState(false);
    const [
        showAdultHouseholdMemberWarning,
        setShowAdultHouseholdMemberWarning,
    ] = useState(false);

    const adolescentMinimumAge = useAtomValue(adolescentMinimumAgeAtom);

    const handleRefetch = async () => {
        const updatedProfile = await refetch();

        if (updatedProfile?.error) {
            toast.addToast({
                type: 'error',
                message: ProfileResources.RefreshDataError,
                autoClose: true,
            });
            return;
        }

        const updatedChildren: SelectChildDto[] =
            updatedProfile?.data?.Kids.map((child) => ({
                ID: child.User.ID.toString(),
                Uid: child.User.UID,
                FullName: child.User.FullName,
            }));

        setChildren(updatedChildren ?? []);
    };

    return (
        <Card alwaysCard={false}>
            <VerticalContainer spacing="md">
                <PfaLogoIcon className="mb-2 w-24" />
                <h1 className="h2">{R.WhoNeedsPhysiotherapy}</h1>

                <NavigationItem
                    onClick={() => onSelectPatient(parentUid)}
                    caption={format(BookingResources.Myself, parentFirstName)}
                    icon={
                        parentGender === SexEnum.Female ? (
                            <WomanIcon className="icon-box" />
                        ) : (
                            <ManIcon className="icon-box" />
                        )
                    }
                />

                {children?.map((child) => (
                    <NavigationItem
                        key={child.Uid}
                        onClick={() => onSelectPatient(child.Uid)}
                        caption={child.FullName}
                        icon={<ChildIcon className="icon-box" />}
                    />
                ))}

                <NavigationItem
                    onClick={() => setShowAddChildModal(true)}
                    caption={format(
                        BookingResources.AddChildUnderCertainAge,
                        adolescentMinimumAge.toString(),
                    )}
                />

                <NavigationItem
                    onClick={() => setShowAdultHouseholdMemberWarning(true)}
                    caption={format(
                        BookingResources.OtherHouseholdmembers,
                        adolescentMinimumAge.toString(),
                    )}
                />
            </VerticalContainer>

            {showAddChildModal && (
                <AddChildrenModal
                    title={ProfileResources.ChooseChildren}
                    allowedTypes={[HouseholdMemberType.Child]}
                    mode={AddChildrenMode.Booking}
                    onClose={() => setShowAddChildModal(false)}
                    onChildAdded={(uuid) => {
                        setShowAddChildModal(false);
                        if (uuid) onSelectPatient(uuid);
                        void handleRefetch();
                    }}
                />
            )}

            {showAdultHouseholdMemberWarning && (
                <AdultHouseholdMemberWarningModal
                    onClose={() => setShowAdultHouseholdMemberWarning(false)}
                    adolescentMinimumAge={adolescentMinimumAge}
                />
            )}
        </Card>
    );
};
